import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Search, 
  Filter,
  Users,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  Eye,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users/?role=client`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.email) {
      toast.error('L\'email est obligatoire');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/users/client`, newClient);
      toast.success(`Client créé ! Mot de passe temporaire: ${response.data.temp_password}`);
      setShowCreateModal(false);
      setNewClient({ email: '', first_name: '', last_name: '', company_name: '', phone: '' });
      fetchClients();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
    const company = (c.company_name || '').toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) || company.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Clients
            </h1>
            <p className="text-[#64748b] mt-1">
              {filteredClients.length} client(s) enregistré(s)
            </p>
          </div>
          
          <Button 
            onClick={() => setShowCreateModal(true)}
            style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
            className="font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau client
          </Button>
        </div>

        {/* Search */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
              <Input
                placeholder="Rechercher un client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-default pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Clients Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                Aucun client trouvé
              </h3>
              <p className="text-[#64748b]">
                Ajoutez votre premier client pour commencer
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
              <Card key={client.id} className="card card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <Avatar 
                      name={`${client.first_name} ${client.last_name}`}
                      size="lg"
                      type="client"
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir le profil
                        </DropdownMenuItem>
                        <DropdownMenuItem>Modifier</DropdownMenuItem>
                        <DropdownMenuItem>Voir les projets</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <h3 className="font-semibold mb-1" style={{ color: theme.colors?.primary }}>
                    {client.first_name} {client.last_name}
                  </h3>
                  
                  {client.company_name && (
                    <p className="text-sm text-[#64748b] flex items-center gap-1 mb-3">
                      <Building2 className="w-3 h-3" />
                      {client.company_name}
                    </p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    {client.email && (
                      <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-[#64748b] hover:text-[var(--color-accent)]">
                        <Mail className="w-4 h-4" />
                        {client.email}
                      </a>
                    )}
                    {client.phone && (
                      <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-[#64748b] hover:text-[var(--color-accent)]">
                        <Phone className="w-4 h-4" />
                        {client.phone}
                      </a>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                    <Badge 
                      className={client.is_active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}
                    >
                      {client.is_active !== false ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Client Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouveau client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="client@example.com"
                  className="input-default"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input
                    value={newClient.first_name}
                    onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                    placeholder="Jean"
                    className="input-default"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    value={newClient.last_name}
                    onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                    placeholder="Dupont"
                    className="input-default"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Société</Label>
                <Input
                  value={newClient.company_name}
                  onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                  placeholder="Nom de l'entreprise"
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  className="input-default"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={creating}
                  className="rounded-full" 
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {creating ? 'Création...' : 'Créer le client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ClientsPage;
